<script setup>
import { computed, ref, watch } from 'vue';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import useSiteStore from '../../store/modules/site';
import MultiSelect from '../../components/MultiSelect.vue';

const siteStore = useSiteStore();

const filterModel = ref({
  units: [],
  noms: [],
  dateRange: [],
  selectTypes: [],
});

const unitsOptions = ref(window.FILTER_UNITS_OPTIONS || []);
const nomsOptions = ref(window.FILTER_NOMS_OPTIONS || []);
const typeOptions = [
  { label: `${translator.t('main', 'Matches')}`, value: 1 },
  { label: `${translator.t('main', 'Season tickets')}`, value: 2 },
  { label: `${translator.t('main', 'Services')}`, value: 3 },
  { label: `${translator.t('main', 'Events')}`, value: 4 },
];

const showClearButton = computed(
  () => filterModel.value.units.length
		|| filterModel.value.noms.length
		|| filterModel.value.dateRange.length
		|| filterModel.value.selectTypes.length,
);

const isMobile = computed(() => window.innerWidth <= 991);
const showFilters = isMobile.value ? ref(false) : ref(true);

function getDateRangeParam() {
  if (!filterModel.value.dateRange[0]) return [];
  return `${filterModel.value.dateRange[0]} — ${filterModel.value.dateRange[1]}`;
}

function applyFilter() {
  siteStore.setSelectTypes(filterModel.value.selectTypes);
  const searchParams = {
    dateRange: getDateRangeParam(),
    objectTags: filterModel.value.units,
    nomTags: filterModel.value.noms,
  };
  siteStore.searchEvents({ searchParams });
  siteStore.searchPerformances({ searchParams });
  siteStore.searchSubscriptions({ searchParams });
  siteStore.searchServices({ searchParams });
}

function clearFilters() {
  filterModel.value.units = [];
  filterModel.value.noms = [];
  filterModel.value.dateRange = [];
  filterModel.value.selectTypes = [];
}

watch(filterModel.value, () => applyFilter());
</script>

<template>
  <meta
    name="viewport"
    content="
      width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no
    "
  >
  <div class="event-banner-filter">
    <div class="container">
      <div class="event-banner-filter__blur" />
      <div
        v-if="isMobile"
        class="d-flex justify-content-between text-white fw-bold position-relative"
        @click="showFilters = !showFilters"
      >
        {{ translator.t("main", "Filters") }}
        <svg
          width="20"
          height="20"
          color="white"
          class="mr-1 i-filter__icon"
          :class="{ active: showFilters }"
        >
          <use xlink:href="#filter-down" />
        </svg>
      </div>
      <div
        v-show="showFilters"
        class="filter-wrapper flex-column flex-lg-row align-items-start align-items-lg-center gap-3 flex-wrap"
      >
        <MultiSelect
          v-model="filterModel.units"
          class="m-0 w-100 w-lg-25"
          :options="unitsOptions"
          :multiple-label="`${translator.t('main', 'Areas')}:`"
          :placeholder="translator.t('main', 'All areas')"
          :no-options-text="translator.t('main', 'The list is empty')"
        />
        <MultiSelect
          v-model="filterModel.noms"
          class="m-0 w-100 w-lg-25"
          :options="nomsOptions"
          :multiple-label="`${translator.t('main', 'Tournaments')}:`"
          :placeholder="translator.t('main', 'All tournaments')"
          :no-options-text="translator.t('main', 'The list is empty')"
        />
        <div
          v-if="showClearButton"
          class="d-flex align-items-center text-white flex-grow-1 justify-content-end"
          role="button"
          @click="clearFilters"
        >
          <svg
            height="15"
            width="15" color="white" class="mr-1"
          >
            <use xlink:href="#close" />
          </svg>
          {{ translator.t("main", "Reset") }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@vueform/multiselect/themes/default.css";
.filter-wrapper {
	display: flex;
}

.i-filter {
	&__icon {
		transition: transform 0.3s;
		&.active {
			transform: rotate(-180deg);
		}
	}
}

.event-banner-filter {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  background: rgba(36, 38, 54, 0.28);
  border-radius: 0 0 15px 15px;
  padding: 28px 0;

  &__blur {
    position: absolute;
    inset: 0;
    border-radius: 0 0 12px 12px;
    backdrop-filter: blur(5px);
  }

  @media (max-width: $screen-xs) {
    padding: 16px 0;
  }
}
</style>
